import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrokerLoopWebSocketSDK,
  QueuedSessionState,
  useSession,
  type ActiveSessionState,
  type InactiveSessionState,
} from "@financeable/brokerloop-data-plane";
import {
  setLodgeAIAutomation,
  setLodgeAIError,
  lodgeAISessionSelector,
  setLodgeAIQueue,
  setLodgeAISession,
  resetLodgeAIQueue,
  lodgeAIQueueSelector,
} from "@/store/slices/lodgeAISlice";
import { type LodgeAIAutomation } from "@/store/slices/types/lodgeAITypes";
import { LodgeAIContext } from "./LodgeAIContext";
import { workflowStatusAbstraction } from "./utils/workflowStatusAbstraction";
import { isActiveSessionState, isInactiveSessionState, isQueuedSessionState } from "./utils/sessionStateTypeGuards";

const LodgeAIProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const lodgeAIQueue = useSelector(lodgeAIQueueSelector);
  const { sessionId, jwt } = useSelector(lodgeAISessionSelector) ?? {};
  const [sdk, _setSdk] = useState<BrokerLoopWebSocketSDK>(
    new BrokerLoopWebSocketSDK({
      url: import.meta.env.VITE_DATA_PLANE_URL,
      autoConnect: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      debug: true,
      enforceMessageOrder: false,
    }),
  );
  const { state: sessionState, actions: sessionActions } = useSession({
    sdk,
    sessionId: sessionId ?? "",
    jwtToken: jwt ?? "",
  });

  const isActiveSession = isActiveSessionState(sessionState);
  const isInactiveSession = isInactiveSessionState(sessionState);
  const isActiveOrInactiveSession = isActiveSession || isInactiveSession;

  // Sync session state to LodgeAI automation state.
  useEffect(() => {
    if (isActiveOrInactiveSession) {
      // Abstraction of workflow based on different statuses.
      const updatedSessionState = workflowStatusAbstraction(sessionState);
      dispatch(setLodgeAIAutomation({ ...updatedSessionState }));
    }
  }, [
    sessionState?.lastUpdated,
    (sessionState as ActiveSessionState | InactiveSessionState | undefined)?.workflowState?.status,
    sessionState?.type,
    isActiveOrInactiveSession,
    lodgeAIQueue,
    dispatch,
  ]);

  // When the session's connection state changes, update the LodgeAI automation state.
  useEffect(() => {
    dispatch(
      setLodgeAIAutomation({
        connectionState: sessionState?.connectionState,
      } as LodgeAIAutomation),
    );
  }, [sessionState?.connectionState, dispatch]);

  // Sync session state queue position to LodgeAI queue state.
  useEffect(() => {
    if (isQueuedSessionState(sessionState)) {
      const { queuePosition } = sessionState;
      if (queuePosition != null) {
        dispatch(setLodgeAIQueue({ queuePosition, inProgress: !!queuePosition }));
      }
    }
  }, [sessionState?.type, (sessionState as QueuedSessionState)?.queuePosition, dispatch]);

  // If the LodgeAI queue state is defined, reset it
  // as the session state has progressed to either active or inactive.
  useEffect(() => {
    if (isActiveSession && !!lodgeAIQueue) {
      dispatch(resetLodgeAIQueue());
    }

    if (isInactiveSession && !!lodgeAIQueue) {
      dispatch(resetLodgeAIQueue());
      dispatch(
        setLodgeAIError({
          title: "Session no longer available",
          message: "The LodgeAI session is no longer active. Please try submitting again.",
        }),
      );
    }
  }, [sessionState?.type, dispatch]);

  // Sync LodgeAI expiresAt time, which has an extension of +2 min from session creation
  // and +5 minutes on other events except fatal error.
  // The expiresAt is a type in the LodgeAI session as it is also returned from the initial control plane call.
  useEffect(() => {
    if (isActiveSessionState(sessionState)) {
      dispatch(setLodgeAISession({ expiresAt: sessionState.expiresAt }));
    }
  }, [sessionState?.lastUpdated, dispatch]);

  // Storing SDK in context because it is not recommended to store non-serialized data in Redux.
  return (
    <LodgeAIContext.Provider
      value={{
        sdk,
        sessionActions,
      }}
    >
      {children}
    </LodgeAIContext.Provider>
  );
};

export { LodgeAIProvider };
